import React from 'react';
import { Box, Button, Container, Grid, Paper, Stack, Typography } from '@mui/material';
import BuildCircleRoundedIcon from '@mui/icons-material/BuildCircleRounded';
import DeviceHubRoundedIcon from '@mui/icons-material/DeviceHubRounded';
import CloudDoneRoundedIcon from '@mui/icons-material/CloudDoneRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { alpha } from '@mui/material/styles';
import Seo from '../common/Seo';
import { servicePages } from '../../servicePagesData';

const services = [
	{
		icon: <DeviceHubRoundedIcon fontSize="small" />,
		title: 'Systems Integration',
		description: 'Connect internal platforms, vendor tools, and data pipelines into a more reliable operating flow.',
		points: ['API design and implementation', 'Workflow automation', 'Tooling consolidation'],
		path: '/cloud-architecture',
	},
	{
		icon: <BuildCircleRoundedIcon fontSize="small" />,
		title: 'Software Modernization',
		description: 'Refine legacy codebases, improve architecture decisions, and create a cleaner path for shipping.',
		points: ['Refactors and platform cleanup', 'Experience and UX improvement', 'Engineering process upgrades'],
		path: '/custom-software-development',
	},
	{
		icon: <CloudDoneRoundedIcon fontSize="small" />,
		title: 'DevOps and Reliability',
		description: 'Build dependable infrastructure, release workflows, and observability so delivery becomes calmer and safer.',
		points: ['CI/CD and deployment design', 'Cloud environment hardening', 'Monitoring and incident readiness'],
		path: '/devops-automation',
	},
];

const processSteps = [
	{
		step: '01',
		title: 'Discovery',
		description: 'We align on goals, current pain points, and what success should look like operationally and commercially.',
	},
	{
		step: '02',
		title: 'Architecture',
		description: 'We define the technical direction, identify constraints, and choose the work that creates the highest leverage.',
	},
	{
		step: '03',
		title: 'Execution',
		description: 'Implementation is handled with quality controls, clear communication, and decisions that support long-term maintainability.',
	},
	{
		step: '04',
		title: 'Stabilization',
		description: 'We polish the final system, improve handoff quality, and leave the team with something easier to run and extend.',
	},
];

const engagementOptions = [
	{
		title: 'Focused Sprint',
		description: 'Best for a specific modernization effort, integration project, or UX improvement initiative.',
		cta: 'Discuss a sprint',
	},
	{
		title: 'Project Delivery',
		description: 'Best for larger end-to-end builds that need architecture, implementation, and reliable launch support.',
		cta: 'Plan a project',
		featured: true,
	},
	{
		title: 'Ongoing Advisory',
		description: 'Best for teams that want a technical partner supporting architecture, infrastructure, and delivery quality over time.',
		cta: 'Explore advisory',
	},
];

const Services = () => {
	return (
		<>
			<Seo
				title="Services - BrokenCosmos Labs"
				description="Explore BrokenCosmos Labs services for systems integration, software modernization, AI integration, and dependable DevOps execution for Canadian teams."
				path="/services"
			/>
			<Box id="services" sx={{ py: { xs: 10, md: 14 } }}>
				<Container maxWidth="xl">
					<motion.div initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
						<Typography variant="overline" sx={{ color: '#93c5fd', letterSpacing: '0.12em' }}>
							Services
						</Typography>
						<Typography variant="h2" component="h1" sx={{ mt: 1.5, mb: 2.5, maxWidth: '12ch' }}>
							Technical work that improves the whole business.
						</Typography>
						<Typography variant="body1" sx={{ maxWidth: 760, mb: 7 }}>
							We focus on the work that makes operations smoother, products stronger, and delivery more credible. Each
							engagement is tailored, but the through line is always the same: less friction, better systems, and a more
							professional end result across Toronto, the GTA, and Canada-wide teams.
						</Typography>
					</motion.div>

					<Grid container spacing={3}>
						{services.map((service, index) => (
							<Grid item xs={12} md={4} key={service.title}>
								<motion.div
									initial={{ opacity: 0, y: 28 }}
									whileInView={{ opacity: 1, y: 0 }}
									viewport={{ once: true }}
									transition={{ duration: 0.45, delay: index * 0.12 }}
									style={{ height: '100%' }}
								>
									<Paper
										elevation={0}
										sx={{
											height: '100%',
											p: 3.25,
											borderRadius: '28px',
											backgroundColor: alpha('#0f172a', 0.72),
										}}
									>
										<Box
											sx={{
												width: 48,
												height: 48,
												borderRadius: '16px',
												display: 'grid',
												placeItems: 'center',
												mb: 2.5,
												backgroundColor: alpha('#2563eb', 0.14),
												color: '#93c5fd',
											}}
										>
											{service.icon}
										</Box>
										<Typography variant="h4" component="h2" sx={{ mb: 1.5 }}>
											{service.title}
										</Typography>
										<Typography variant="body2" sx={{ mb: 2.5 }}>
											{service.description}
										</Typography>
										<Stack spacing={1.2}>
											{service.points.map((point) => (
												<Typography key={point} variant="body2" sx={{ color: 'text.primary' }}>
													{point}
												</Typography>
											))}
										</Stack>
										<Button component={Link} to={service.path} variant="outlined" color="secondary" sx={{ mt: 2.5 }}>
											Learn More
										</Button>
									</Paper>
								</motion.div>
							</Grid>
						))}
					</Grid>

					<Box sx={{ mt: { xs: 6, md: 8 } }}>
						<Typography variant="h3" component="h2" sx={{ mb: 1.6 }}>
							Dedicated service pages
						</Typography>
						<Typography variant="body1" sx={{ mb: 3.5, maxWidth: 780 }}>
							If you are evaluating one specific capability, these pages break down scope and outcomes in more detail.
						</Typography>
						<Grid container spacing={2.5}>
							{servicePages.map((item) => (
								<Grid item xs={12} md={6} lg={3} key={item.key}>
									<Paper
										elevation={0}
										sx={{
											p: 2.5,
											height: '100%',
											borderRadius: '22px',
											backgroundColor: alpha('#0b1729', 0.65),
										}}
									>
										<Typography variant="h6" sx={{ mb: 1 }}>
											{item.kicker}
										</Typography>
										<Button component={Link} to={item.path} variant="text" sx={{ px: 0 }}>
											View service page
										</Button>
									</Paper>
								</Grid>
							))}
						</Grid>
					</Box>
				</Container>
			</Box>
		</>
	);
};

const ProcessSection = () => (
	<Box sx={{ py: { xs: 10, md: 14 } }}>
		<Container maxWidth="xl">
			<Typography variant="h2" component="h2" sx={{ textAlign: 'center', mb: 2 }}>
				How engagements usually run
			</Typography>
			<Typography variant="body1" sx={{ textAlign: 'center', maxWidth: 760, mx: 'auto', mb: 7 }}>
				We keep the process structured enough to stay reliable and lightweight enough to keep momentum.
			</Typography>
			<Grid container spacing={3}>
				{processSteps.map((item) => (
					<Grid item xs={12} md={6} lg={3} key={item.step}>
						<Paper
							elevation={0}
							sx={{
								height: '100%',
								p: 3,
								borderRadius: '24px',
								backgroundColor: alpha('#0b1729', 0.68),
							}}
						>
							<Typography variant="h3" sx={{ color: alpha('#60a5fa', 0.3), mb: 2 }}>
								{item.step}
							</Typography>
							<Typography variant="h5" sx={{ mb: 1.2 }}>
								{item.title}
							</Typography>
							<Typography variant="body2">{item.description}</Typography>
						</Paper>
					</Grid>
				))}
			</Grid>
		</Container>
	</Box>
);

const PricingSection = () => (
	<Box sx={{ py: { xs: 10, md: 14 } }}>
		<Container maxWidth="xl">
			<Typography variant="h2" component="h2" sx={{ textAlign: 'center', mb: 2 }}>
				Flexible engagement models
			</Typography>
			<Typography variant="body1" sx={{ textAlign: 'center', maxWidth: 760, mx: 'auto', mb: 7 }}>
				Most work starts with a conversation rather than a fixed package. These are common ways we partner with clients.
			</Typography>
			<Grid container spacing={3} alignItems="stretch">
				{engagementOptions.map((option) => (
					<Grid item xs={12} md={4} key={option.title}>
						<Paper
							elevation={0}
							sx={{
								height: '100%',
								p: 4,
								borderRadius: '28px',
								display: 'flex',
								flexDirection: 'column',
								background: option.featured
									? 'linear-gradient(180deg, rgba(37,99,235,0.28) 0%, rgba(15,27,45,0.82) 100%)'
									: alpha('#0f172a', 0.7),
								border: option.featured
									? `1px solid ${alpha('#93c5fd', 0.25)}`
									: `1px solid ${alpha('#e2e8f0', 0.08)}`,
							}}
						>
							<Typography variant="h4" sx={{ mb: 1.5 }}>
								{option.title}
							</Typography>
							<Typography variant="body2" sx={{ mb: 4, flexGrow: 1 }}>
								{option.description}
							</Typography>
							<Button
								component={Link}
								to="/contact"
								variant={option.featured ? 'contained' : 'outlined'}
								color={option.featured ? 'primary' : 'secondary'}
								endIcon={<ArrowForwardRoundedIcon />}
							>
								{option.cta}
							</Button>
						</Paper>
					</Grid>
				))}
			</Grid>
		</Container>
	</Box>
);

const ServicesPage = () => (
	<>
		<Services />
		<ProcessSection />
		<PricingSection />
	</>
);

export default ServicesPage;
